import React from 'react';
import {Text, View, ImageRequireSource, ImageStyle} from 'react-native';
import {images} from '../../../assets/icons';
import {ECard, Transport} from '../../../assets/images';
import {ContactsCard} from '../../../shared/components/contactsCard';
import {ContentWithRightImage} from '../../../shared/components/contentWithRightImage';
import {Hr} from '../../../shared/components/hr';
import {Html} from '../../../shared/components/html';
import {Image} from '../../../shared/components/image';
import {ResponsiveView} from '../../../shared/components/responsiveView';
import {useStyles} from '../../../shared/hooks/styles';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {formatPhone, formatPhoneToCallable, formatUrl} from '../../../shared/pipes';
import {openExternalLink, openExternalPhone} from '../../../shared/pipes/openExternalLinks';
import {DeviceSize} from '../../../shared/theme/helpers';
import {CardFeatures} from '../components/cardFeatures';
import {CardReplenishment} from '../components/cardReplenishment';
import {ECardInfo} from '../components/eCardInfo';
import {Props} from './model';

const RIGHT_IMAGE_SIZE_MULTIPLIER = 0.9;

export default ({content}: Props) => {
  const isDesktop = useIsDesktop();
  const contactsImageSize = {
    width: isDesktop ? 159 : 288,
    height: isDesktop ? 95 : 172,
  };
  const styles = useStyles(({theme, mediaOnly}) => {
    return {
      container: {
        ...mediaOnly(DeviceSize.desktop, {marginBottom: theme.screen.bottomOffset}, {}),
      },
      sectionWithImageContainer: {
        ...mediaOnly(DeviceSize.desktop, {paddingBottom: 13}, {paddingBottom: 69}),
      },
      sectionWithImageContent: mediaOnly(DeviceSize.desktop, {
        minHeight: 1,
        maxWidth: '100%',
        width: 820,
      }),
      sectionWithImageBg: mediaOnly(DeviceSize.desktop, {
        width: 444,
        height: 457,
        top: -93,
      }),
      sectionWithImageIcon: mediaOnly(DeviceSize.desktop, {
        marginTop: -110,
        right: 0,
      }),
      featuresTitle: {
        ...mediaOnly(
          DeviceSize.desktop,
          {
            ...theme.fonts.h5,
          },
          {
            ...theme.fonts.bodySmallLight,
          },
        ),
        color: theme.color.textColor,
      },
      featuresTitleWrapper: {},
      cardInfoWrapper: mediaOnly(
        DeviceSize.desktop,
        {
          marginBottom: 30,
        },
        {
          marginBottom: 20,
        },
      ),
      cardReplenishmentWrapper: mediaOnly(
        DeviceSize.desktop,
        {
          paddingBottom: 40,
        },
        {
          marginBottom: 39,
        },
      ),
      descriptionMobile: theme.fonts.bodySmallLight,
      contactsImage: mediaOnly(
        DeviceSize.desktop,
        {},
        {
          width: contactsImageSize.width,
        },
      ),
      contactsImageWrapper: mediaOnly(
        DeviceSize.desktop,
        {
          marginRight: 30,
        },
        {
          width: contactsImageSize.width,
          marginBottom: 34,
        },
      ),
    };
  });

  const bottomContent = (
    <>
      <ResponsiveView only={[DeviceSize.desktop]}>
        <CardFeatures />
      </ResponsiveView>
      <Hr marginTop={isDesktop ? 18 : 30} marginBottom={isDesktop ? 24 : 30} />
      <ContactsCard
        imageWrapperStyles={styles.contactsImageWrapper}
        contacts={[
          {
            icon: images.Phone,
            value: formatPhone(content.services.transportService.nonFinancial.eCardSupportPhone),
            onPress: () => {
              openExternalPhone(
                formatPhoneToCallable(content.services.transportService.nonFinancial.eCardSupportPhone),
              );
            },
          },
          {
            icon: images.Globe,
            value: formatUrl(content.ekartaContacts.website),
            onPress: () => {
              openExternalLink(content.ekartaContacts.website);
            },
          },
        ]}
        imageStyle={styles.contactsImage as ImageStyle}
        imageProps={{
          width: isDesktop ? 159 : 288,
          height: isDesktop ? 95 : 172,
          source: ECard as unknown as ImageRequireSource,
        }}
      />
    </>
  );

  return (
    <View style={styles.container}>
      <ContentWithRightImage
        containerStyle={styles.sectionWithImageContainer}
        contentStyle={styles.sectionWithImageContent}
        backgroundStyle={styles.sectionWithImageBg}
        iconStyle={styles.sectionWithImageIcon}
        icon={
          <Image
            source={Transport as any}
            width={580 * RIGHT_IMAGE_SIZE_MULTIPLIER}
            height={569 * RIGHT_IMAGE_SIZE_MULTIPLIER}
          />
        }>
        <View style={styles.cardInfoWrapper}>
          <ECardInfo />
        </View>
        <View style={styles.cardReplenishmentWrapper}>
          <CardReplenishment />
        </View>
        <View style={styles.featuresTitleWrapper}>
          {isDesktop ? (
            <Text testID='nonfinancial-transport-card-description-id' style={styles.featuresTitle}>
              {content.services.transportService.nonFinancial.descriptionDesktop}
            </Text>
          ) : (
            <Html
              baseFontStyle={styles.descriptionMobile}
              content={content.services.transportService.nonFinancial.descriptionMobile}
              testID='eCard-replenishment-tips'
            />
          )}
        </View>
        <ResponsiveView exclude={[DeviceSize.desktop]}>{bottomContent}</ResponsiveView>
      </ContentWithRightImage>
      <ResponsiveView only={[DeviceSize.desktop]}>{bottomContent}</ResponsiveView>
    </View>
  );
};
